// Curved VR HUD: the flat 2D panel (game.gui, laid out in px) is rendered into a
// texture every frame and shown on the INSIDE of a cylinder segment around the
// viewer, so the left/right edges are as close as the centre — it reads as round
// inside the headset's oval view (user, 2026-10-10: "curve the plane away from my
// face, left and right edges closer than the center"). Controller rays are mapped
// from the cylinder hit back onto the hidden flat panel, so hover/click/keyboard
// keep working unchanged in the UI input core.
import {
	BackSide,
	CylinderGeometry,
	LinearFilter,
	LinearMipmapLinearFilter,
	Mesh,
	MeshBasicMaterial,
	OrthographicCamera,
	Quaternion,
	RepeatWrapping,
	RGBAFormat,
	SRGBColorSpace,
	UnsignedByteType,
	Vector3,
	WebGLRenderTarget,
} from 'three';

const TEXTURE_SCALE = 2;   // texture px per layout px (text stays crisp on the headset)
const SEGMENTS = 48;

const _q = new Quaternion();
const _n = new Vector3();
const _p = new Vector3();
const _s = new Vector3();

export class CurvedHud {
	constructor({ renderer, gui }) {
		this.renderer = renderer;
		this.gui = gui;
		this.mesh = null;
		this.rt = null;
		// NOT a child of the gui: a camera inside a scaled parent renders nothing (three,
		// measured 2026-10-10). Its world pose + frustum are set from the gui each render.
		this.camera = new OrthographicCamera(-1, 1, 1, -1, 0.001, 10);
		this.layoutWidth = 0;
		this.layoutHeight = 0;
		this.radius = 1;
		this.widthMetres = 1;
		this._hitPatched = null;
		this.enabled = false;
	}

	// (re)build the texture and the cylinder for a layout of w×h px shown widthMetres
	// wide at radius metres from the viewer
	configure({ layoutWidth, layoutHeight, widthMetres, radius }) {
		if (this.layoutWidth === layoutWidth && this.layoutHeight === layoutHeight && this.widthMetres === widthMetres && this.radius === radius && this.mesh) return;
		this.layoutWidth = layoutWidth; this.layoutHeight = layoutHeight; this.widthMetres = widthMetres; this.radius = radius;
		this.rt?.dispose();
		this.rt = new WebGLRenderTarget(layoutWidth * TEXTURE_SCALE, layoutHeight * TEXTURE_SCALE, {
			format: RGBAFormat, type: UnsignedByteType, depthBuffer: false, stencilBuffer: true,
			colorSpace: this.renderer.outputColorSpace, minFilter: LinearMipmapLinearFilter, magFilter: LinearFilter, generateMipmaps: true,
		});
		this.rt.isXRRenderTarget = true; // same output colour handling as the canvas/eye buffers (see client/screenshot.js)
		// seen from INSIDE the cylinder the texture would be mirrored — flip it
		this.rt.texture.wrapS = RepeatWrapping;
		this.rt.texture.repeat.x = -1;
		const theta = widthMetres / radius;                 // arc length == panel width
		const heightMetres = widthMetres * layoutHeight / layoutWidth;
		const geometry = new CylinderGeometry(radius, radius, heightMetres, SEGMENTS, 1, true, Math.PI - theta / 2, theta); // centred on −z
		if (this.mesh) { this.mesh.geometry.dispose(); this.mesh.geometry = geometry; this.mesh.material.map = this.rt.texture; this.mesh.material.needsUpdate = true; }
		else {
			this.mesh = new Mesh(geometry, new MeshBasicMaterial({ map: this.rt.texture, transparent: true, depthTest: false, depthWrite: false, side: BackSide }));
			this.mesh.name = 'CURVED_HUD';
			this.mesh.renderOrder = 20000;
			this.mesh.frustumCulled = false;
		}
		this.theta = theta;
		this.heightMetres = heightMetres;
	}

	// draw the flat panel into the texture (XR off + render target restored, like the planet bakes)
	render() {
		if (!this.rt) return;
		const r = this.renderer;
		const prevRT = r.getRenderTarget(), xrWas = r.xr.enabled, wasVisible = this.gui.visible;
		// camera 1 m in front of the panel plane, frustum = the layout rect in WORLD units
		this.gui.updateMatrixWorld(true);
		const sc = this.gui.getWorldScale(_s).x;
		this.gui.getWorldQuaternion(_q);
		this.gui.getWorldPosition(_p);
		const cam = this.camera;
		cam.left = -this.layoutWidth / 2 * sc; cam.right = this.layoutWidth / 2 * sc;
		cam.top = this.layoutHeight / 2 * sc; cam.bottom = -this.layoutHeight / 2 * sc;
		cam.updateProjectionMatrix();
		cam.position.copy(_p).add(_n.set(0, 0, 1).applyQuaternion(_q));
		cam.quaternion.copy(_q);
		cam.updateMatrixWorld(true);
		this.gui.visible = true;
		r.xr.enabled = false;
		r.setRenderTarget(this.rt);
		r.setClearColor(0x000000, 0);
		r.clear(true, false, true);
		r.render(this.gui, this.camera);
		r.setRenderTarget(prevRT);
		r.xr.enabled = xrWas;
		this.gui.visible = wasVisible;
	}

	// map a controller ray that hits the cylinder onto the hidden flat panel: returns the
	// raycaster re-aimed perpendicular at the mapped panel point (null = no cylinder hit)
	remap(raycaster) {
		if (!this.mesh || !this.enabled) return null;
		const hit = raycaster.intersectObject(this.mesh, false)[0];
		if (!hit || !hit.uv) return null;
		const u = 1 - hit.uv.x, v = hit.uv.y;               // repeat.x = -1 mirrors u
		_p.set((u - 0.5) * this.layoutWidth, (v - 0.5) * this.layoutHeight, 0);
		this.gui.localToWorld(_p);
		this.gui.getWorldQuaternion(_q);
		_n.set(0, 0, 1).applyQuaternion(_q);
		raycaster.set(_p.clone().addScaledVector(_n, 0.05), _n.clone().negate());
		raycaster.far = 0.2;
		return raycaster;
	}

	// patch the UI input core's controller ray so every XR hover/press lands on the panel
	patchHitTester(hitTester) {
		if (!hitTester || this._hitPatched === hitTester) return;
		const orig = hitTester.setFromController.bind(hitTester);
		const self = this;
		hitTester.setFromController = function (controller) {
			const rc = orig(controller);
			const prevFar = rc.far;
			if (self.remap(rc)) return rc;
			rc.far = prevFar;
			return rc;
		};
		this._hitPatched = hitTester;
	}
}
